export interface SmoothScrollOptions {
  duration?: number;
  easing?: (t: number) => number;
  offset?: number;
  highlight?: boolean;
}

// Fonction d'easing personnalisée pour une animation plus fluide
const easeInOutCubic = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
};

// Fonction d'easing plus rapide et réactive
const easeOutQuart = (t: number): number => {
  return 1 - (--t) * t * t * t;
};

export const smoothScrollTo = (
  targetId: string,
  options: SmoothScrollOptions = {}
): void => {
  const {
    duration = 800, // Durée réduite par défaut pour plus de réactivité
    easing = easeOutQuart, // Utilise l'easing plus rapide par défaut
    offset = -100, // Offset pour tenir compte de la navbar fixe
    highlight = true,
  } = options;

  const target = document.getElementById(targetId.replace('#', ''));
  if (!target) {
    console.warn(`Element with id "${targetId}" not found`);
    return;
  }

  // Désactiver le smooth scroll CSS pendant l'animation
  document.documentElement.classList.add('js-smooth-scroll');

  const startPosition = window.pageYOffset;
  const targetPosition = target.getBoundingClientRect().top + window.pageYOffset + offset;
  const distance = targetPosition - startPosition;
  let startTime: number | null = null;

  const animation = (currentTime: number) => {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);

    const easedProgress = easing(progress);
    const currentPosition = startPosition + distance * easedProgress;

    window.scrollTo(0, currentPosition);

    if (progress < 1) {
      requestAnimationFrame(animation);
    } else {
      // Animation terminée
      // Réactiver le smooth scroll CSS
      document.documentElement.classList.remove('js-smooth-scroll');
      
      // Ajouter un effet de highlight si demandé
      if (highlight) {
        target.classList.add('section-highlight');
        setTimeout(() => {
          target.classList.remove('section-highlight');
        }, 600);
      }
    }
  };

  requestAnimationFrame(animation);
};

export const useSmoothScroll = (options: SmoothScrollOptions = {}) => {
  const scrollToSection = (targetId: string) => {
    smoothScrollTo(targetId, options);
  };

  // Version ultra-rapide pour une réactivité maximale
  const quickScrollToSection = (targetId: string) => {
    smoothScrollTo(targetId, { 
      ...options, 
      duration: 500, 
      easing: easeOutQuart,
      highlight: false // Pas d'effet de highlight pour la version rapide
    });
  };

  return { scrollToSection, quickScrollToSection };
}; 